/**
 * RenderPipeline.ts — orchestrator that owns the ordered list of passes
 * and encodes them sequentially each frame.
 *
 * Adding three.js (stage 5) = inserting a new RenderPass into this list.
 * No frame-loop logic in main.ts beyond `pipeline.encode(...)`.
 */

import type { FrameContext, RenderPass } from "./RenderPass";

export class RenderPipeline {
  private _passes: RenderPass[] = [];

  add(pass: RenderPass): void {
    this._passes.push(pass);
  }

  insertBefore(name: string, pass: RenderPass): void {
    const idx = this._passes.findIndex((p) => p.name === name);
    if (idx < 0) this._passes.push(pass);
    else this._passes.splice(idx, 0, pass);
  }

  remove(name: string): void {
    const idx = this._passes.findIndex((p) => p.name === name);
    if (idx >= 0) {
      const [pass] = this._passes.splice(idx, 1);
      pass?.destroy?.();
    }
  }

  get passes(): readonly RenderPass[] {
    return this._passes;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    for (const pass of this._passes) {
      pass.encode(encoder, frame);
    }
  }

  destroy(): void {
    for (const p of this._passes) p.destroy?.();
    this._passes.length = 0;
  }
}
